// Approval-state utilities
// -------------------------
// Single source of truth for "how many paragraphs in a report section need
// review, which have been approved, and is the section therefore fully
// approved?".
//
// Why this exists (business context):
// Every Review Document page (RD_*.js) shows the clinician a progress figure
// ("3 of 5 approved") and, when everything is approved, lets them trigger the
// section render and the Patient Report. Historically the on-screen progress
// (computed in useApprovalTracking) and the persisted "xReportApproved" flag
// (computed independently inside each page's exit handler) were derived by
// SEPARATE pieces of logic. That divergence is exactly how a Patient Report
// could finalise while paragraphs were still unapproved. By funnelling both the
// display hook AND the exit-transaction write through this one pure function,
// the number the clinician sees and the flag we persist can never disagree.
//
// This module is deliberately pure (no React, no Firestore): it takes plain
// data in and returns plain data out, so it is trivial to unit test and safe to
// call from inside a Firestore transaction against a fresh document snapshot.

const EMPTY_SET = new Set();

/**
 * Determine which paragraph codes are "valid" for approval tracking.
 *
 * A code is valid when it appears in the section's docStructure, has non-empty
 * content in the outputs map, and has not been explicitly excluded (e.g. a
 * strength paragraph the clinician deleted this session). Codes with empty or
 * missing content are intentionally ignored: an empty output is not shown to
 * the clinician and therefore cannot be "approved", so counting it would make
 * the section impossible to complete.
 *
 * @param {Object} docStructure  Section -> paragraph map with { code } entries
 * @param {Object} outputs        Map of code -> generated content string
 * @param {Set<string>} excludedCodes  Codes to treat as not-present this session
 * @returns {Set<string>} the set of valid paragraph codes
 */
export const computeValidCodes = (docStructure, outputs, excludedCodes = EMPTY_SET) => {
  const codes = new Set();
  if (!docStructure || !outputs) return codes;

  Object.values(docStructure).forEach((section) => {
    if (!section) return;
    Object.values(section).forEach((entry) => {
      const code = entry?.code;
      if (!code) return;
      if (
        outputs[code] !== undefined &&
        outputs[code] !== '' &&
        !excludedCodes.has(code)
      ) {
        codes.add(code);
      }
    });
  });

  return codes;
};

/**
 * Compute the full approval state for a report section.
 *
 * The approved set is the intersection of the persisted approvedParagraphs keys
 * with the valid codes. The intersection is what keeps orphaned approval keys
 * (paragraphs the backend later blanked or deleted) from inflating the approved
 * count above the total and either blocking or falsely satisfying the gate.
 *
 * isFullyApproved requires at least one valid code: a section with zero valid
 * codes must never report itself as approved (a 0 === 0 comparison would
 * otherwise be a false positive and let an empty section finalise).
 *
 * @param {Object} params
 * @param {Object} params.docStructure       Section -> paragraph map with { code } entries
 * @param {Object} params.outputs            Map of code -> generated content string
 * @param {Object} params.approvedParagraphs Persisted approvals (code -> approval entry)
 * @param {Set<string>} [params.excludedCodes] Codes to treat as not-present this session
 * @returns {{
 *   validCodes: Set<string>,
 *   approvedCodes: Set<string>,
 *   outstandingCodes: Set<string>,
 *   totalParagraphs: number,
 *   isFullyApproved: boolean,
 * }}
 */
export const computeApprovalState = ({
  docStructure,
  outputs,
  approvedParagraphs,
  excludedCodes = EMPTY_SET,
}) => {
  const validCodes = computeValidCodes(docStructure, outputs, excludedCodes);

  // Only count approvals for codes that are currently valid. This trims stale
  // approval keys that no longer map to a visible, content-bearing paragraph.
  const approvedCodes = new Set(
    Object.keys(approvedParagraphs || {}).filter((code) => validCodes.has(code))
  );

  // Outstanding = valid codes still awaiting approval. This is the list the
  // exit handler and the server-side gate use to explain WHY a section is not
  // yet fully approved.
  const outstandingCodes = new Set(
    [...validCodes].filter((code) => !approvedCodes.has(code))
  );

  const totalParagraphs = validCodes.size;
  const isFullyApproved =
    totalParagraphs > 0 && totalParagraphs === approvedCodes.size;

  return {
    validCodes,
    approvedCodes,
    outstandingCodes,
    totalParagraphs,
    isFullyApproved,
  };
};

/**
 * Find approvals the UI is showing as done but which never persisted.
 *
 * Why this exists (business context):
 * When a clinician taps "Approve", the paragraph turns green immediately
 * (optimistic UI) while the Firestore write happens in the background. Under
 * rapid approve-approve-approve clicking, many transactions race on the same
 * patient document and some can fail outright — after the clinician has
 * already moved on. The screen then shows a paragraph as approved when
 * Firestore holds no approval for it. This was the proven root cause of
 * Patient Reports finalising with missing paragraphs.
 *
 * At exit time we therefore compare the optimistic on-screen set against the
 * authoritative outstanding list derived from a fresh Firestore snapshot
 * (see finalizeSectionReview). Any code in BOTH sets is a "phantom approval":
 * green on screen, absent in the database. The exit flow uses this list to
 * flip those paragraphs back to needs-review and keep the clinician on the
 * page instead of letting them leave believing the section is complete.
 *
 * @param {Set<string>|Array<string>} reviewedCodes  Codes the UI currently shows as approved
 * @param {Set<string>|Array<string>} outstandingCodes  Codes the fresh snapshot says are NOT approved
 * @returns {string[]} codes shown as approved on screen but not persisted
 */
export const computeUnpersistedApprovals = (reviewedCodes, outstandingCodes) => {
  const reviewed = reviewedCodes instanceof Set ? reviewedCodes : new Set(reviewedCodes || []);
  const outstanding = outstandingCodes instanceof Set ? [...outstandingCodes] : (outstandingCodes || []);
  return outstanding.filter((code) => reviewed.has(code));
};
